import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Container, Header, Segment, Card, Button, Message, Table, Modal, Loader, Dimmer, Icon, Divider, Label,
} from 'semantic-ui-react';
import moment from 'moment';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getProfile } from './lib/tickets';
import { getConfiguration, loadStripeForConfig } from './lib/configuration';
import {
    getMemberships, getCurrentMemberships, cancelFreeMembership,
    createSetupIntent, createSubscription, cancelSubscription, reactivateSubscription,
} from './lib/memberships';
import { t } from './locales';
import { formatMoney } from './lib/currency';

function SubscribeForm({ membership, profile, onSubscribed }) {
    const stripe = useStripe();
    const elements = useElements();
    const [error, setError] = useState(null);
    const [processing, setProcessing] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;
        setProcessing(true);
        setError(null);

        const { error: stripeError, setupIntent } = await stripe.confirmSetup({
            elements,
            confirmParams: { return_url: window.location.href },
            redirect: 'if_required',
        });

        if (stripeError) {
            setError(stripeError.message);
            setProcessing(false);
            return;
        }

        const productId = membership.product_id || membership.stripe_price_id || membership.url;
        const result = await createSubscription(setupIntent.payment_method, productId, profile?.email);

        if (!result?.success) {
            setError(t('memberships.subscribeFailed'));
            setProcessing(false);
            return;
        }

        setProcessing(false);
        onSubscribed();
    };

    return (
        <form onSubmit={handleSubmit}>
            <PaymentElement />
            {error && (
                <Message negative style={{ marginTop: '1rem' }}>
                    <Message.Header>{t('memberships.paymentFailed')}</Message.Header>
                    <p>{error}</p>
                </Message>
            )}
            <Button
                fluid
                type="submit"
                disabled={!stripe || processing}
                loading={processing}
                style={{ marginTop: '1.25rem', backgroundColor: 'var(--colour_menu_main)', color: '#fff', borderRadius: 8 }}
            >
                {t('memberships.subscribeButton')}
            </Button>
        </form>
    );
}

function Memberships() {
    const queryClient = useQueryClient();

    const { data: profile } = useQuery({ queryKey: ['ticketing-profile'], queryFn: getProfile });
    const { data: configData } = useQuery({
        queryKey: ['configuration'], queryFn: getConfiguration, staleTime: 60 * 60 * 1000,
    });
    const { data: memberships = [], isLoading: membershipsLoading, error: membershipsError } = useQuery({
        queryKey: ['memberships'], queryFn: getMemberships,
    });
    const { data: currentMemberships = [], isLoading: currentLoading, error: currentError } = useQuery({
        queryKey: ['current-memberships'], queryFn: getCurrentMemberships,
    });

    const [subscribeTarget, setSubscribeTarget] = useState(null);
    const [clientSecret, setClientSecret] = useState(null);
    const [preparingPayment, setPreparingPayment] = useState(false);
    const [prepareError, setPrepareError] = useState(null);
    const [subscribed, setSubscribed] = useState(false);

    const [cancelTarget, setCancelTarget] = useState(null);
    const [cancelling, setCancelling] = useState(false);
    const [actionError, setActionError] = useState(null);

    const stripePromise = (configData?.stripe_api_key || configData?.stripe_connect_account_id)
        ? loadStripeForConfig(configData)
        : null;

    const refreshCurrent = () => queryClient.invalidateQueries(['current-memberships']);

    const openSubscribe = async (membership) => {
        setSubscribeTarget(membership);
        setClientSecret(null);
        setPrepareError(null);
        setSubscribed(false);
        setPreparingPayment(true);
        try {
            const result = await createSetupIntent(profile?.email);
            if (!result?.success) {
                setPrepareError(result?.data || t('memberships.paymentInitError'));
            } else {
                setClientSecret(result.data.client_secret);
            }
        } catch {
            setPrepareError(t('memberships.paymentInitError'));
        }
        setPreparingPayment(false);
    };

    const closeSubscribe = () => {
        setSubscribeTarget(null);
        setClientSecret(null);
        setPrepareError(null);
        setSubscribed(false);
    };

    const handleConfirmCancel = async () => {
        if (!cancelTarget) return;
        setCancelling(true);
        setActionError(null);
        try {
            if (cancelTarget.is_free) {
                const response = await cancelFreeMembership(cancelTarget.id);
                if (response.status !== 204) throw new Error();
            } else {
                const result = await cancelSubscription(cancelTarget.subscription_id);
                if (!result?.success) throw new Error();
            }
            await refreshCurrent();
            setCancelTarget(null);
        } catch {
            setActionError(t('memberships.cancelError'));
        }
        setCancelling(false);
    };

    const handleReactivate = async (membership) => {
        setActionError(null);
        try {
            const result = await reactivateSubscription(membership.subscription_id);
            if (!result?.success) throw new Error();
            await refreshCurrent();
        } catch {
            setActionError(t('memberships.reactivateError'));
        }
    };

    const isLoading = membershipsLoading || currentLoading;

    if (isLoading) {
        return (
            <Dimmer active inverted page>
                <Loader content={t('memberships.loading')} />
            </Dimmer>
        );
    }

    const heldMembershipUrls = new Set(currentMemberships.map((cm) => cm.membership_id));
    const availableMemberships = memberships.filter((m) => !heldMembershipUrls.has(m.url));

    return (
        <Container className="contentContainer" style={{ maxWidth: 860 }}>
            <Header as="h1" className="sectionHeader" style={{ marginBottom: '0.5rem' }}>
                {t('memberships.title')}
            </Header>
            <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                {currentMemberships.length > 0 ? t('memberships.hasMemberships') : t('memberships.noMemberships')}
            </p>

            {(membershipsError || currentError) && (
                <Message negative>
                    <Message.Header>{t('memberships.loadErrorTitle')}</Message.Header>
                    <p>{t('memberships.loadError')}</p>
                </Message>
            )}

            {actionError && <Message negative>{actionError}</Message>}

            {currentMemberships.length > 0 && (
                <Segment style={{ borderRadius: 12 }}>
                    <Header as="h3">
                        <Icon name="id badge outline" />
                        {t('memberships.yourMemberships')}
                    </Header>
                    <Table basic="very" style={{ marginTop: '0.5rem' }}>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>{t('memberships.colName')}</Table.HeaderCell>
                                <Table.HeaderCell>{t('memberships.colStartDate')}</Table.HeaderCell>
                                <Table.HeaderCell>{t('memberships.colEndDate')}</Table.HeaderCell>
                                <Table.HeaderCell>{t('memberships.colCost')}</Table.HeaderCell>
                                <Table.HeaderCell>{t('memberships.colStatus')}</Table.HeaderCell>
                                <Table.HeaderCell />
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {currentMemberships.map((membership) => {
                                const isCancelling = membership.cancel_at_period_end;
                                const isActive = membership.status === 'active' || membership.is_free;
                                return (
                                    <Table.Row key={membership.id}>
                                        <Table.Cell>{membership.name || membership.membership_name}</Table.Cell>
                                        <Table.Cell>
                                            {membership.start_date ? moment(membership.start_date).format('D MMM YYYY') : '—'}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {isCancelling
                                                ? moment(membership.end_date).format('D MMM YYYY')
                                                : (membership.recurring
                                                    ? t('memberships.recurring')
                                                    : (membership.end_date ? moment(membership.end_date).format('D MMM YYYY') : '—'))}
                                        </Table.Cell>
                                        <Table.Cell>{membership.is_free ? t('memberships.free') : formatMoney(membership.cost)}</Table.Cell>
                                        <Table.Cell>
                                            {isCancelling ? (
                                                <Label size="tiny" style={{ background: '#f5d76e' }}>
                                                    {t('memberships.activeUntil', { date: moment(membership.end_date).format('D MMM YYYY') })}
                                                </Label>
                                            ) : (
                                                <Label size="tiny" color={isActive ? 'green' : 'grey'}>
                                                    {membership.status
                                                        ? membership.status[0].toUpperCase() + membership.status.slice(1)
                                                        : (membership.is_free ? t('memberships.active') : '—')}
                                                </Label>
                                            )}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {isCancelling ? (
                                                <Button
                                                    size="small"
                                                    onClick={() => handleReactivate(membership)}
                                                    style={{ backgroundColor: 'var(--colour_menu_main)', color: '#fff' }}
                                                >
                                                    {t('memberships.undoCancellation')}
                                                </Button>
                                            ) : isActive ? (
                                                <Button size="small" negative basic onClick={() => setCancelTarget(membership)}>
                                                    {t('memberships.cancelMembership')}
                                                </Button>
                                            ) : null}
                                        </Table.Cell>
                                    </Table.Row>
                                );
                            })}
                        </Table.Body>
                    </Table>
                </Segment>
            )}

            <Divider hidden />

            {availableMemberships.length > 0 && (
                <Segment style={{ borderRadius: 12 }}>
                    <Header as="h3">
                        <Icon name="star outline" />
                        {t('memberships.availablePlans')}
                    </Header>
                    <Card.Group itemsPerRow={2} stackable>
                        {availableMemberships.map((membership) => (
                            <Card key={membership.url} style={{ borderRadius: 12 }}>
                                <Card.Content>
                                    <Card.Header>{membership.name}</Card.Header>
                                    <Card.Description>{membership.description}</Card.Description>
                                    {membership.monthly_credits > 0 && (
                                        <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
                                            {membership.monthly_credits > 1
                                                ? t('memberships.creditsPerMonth', { count: membership.monthly_credits })
                                                : t('memberships.creditPerMonth', { count: membership.monthly_credits })}
                                        </p>
                                    )}
                                </Card.Content>
                                <Card.Content extra>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <strong>{membership.cost ? `${formatMoney(membership.cost)}/mo` : t('memberships.free')}</strong>
                                        <Button
                                            size="small"
                                            style={{ backgroundColor: 'var(--colour_menu_main)', color: '#fff' }}
                                            onClick={() => openSubscribe(membership)}
                                        >
                                            {t('memberships.selectButton')}
                                        </Button>
                                    </div>
                                </Card.Content>
                            </Card>
                        ))}
                    </Card.Group>
                </Segment>
            )}

            {availableMemberships.length === 0 && currentMemberships.length === 0 && (
                <Segment style={{ borderRadius: 12, textAlign: 'center', color: '#999' }}>
                    {t('memberships.noPlansAvailable')}
                </Segment>
            )}

            {cancelTarget && (
                <Modal open onClose={() => setCancelTarget(null)} size="small">
                    <Modal.Header>{t('memberships.confirmCancelTitle')}</Modal.Header>
                    <Modal.Content>
                        <p>
                            {cancelTarget.is_free
                                ? t('memberships.confirmCancelFree')
                                : t('memberships.confirmCancelPaid', { date: moment(cancelTarget.end_date).format('D MMM YYYY') })}
                        </p>
                    </Modal.Content>
                    <Modal.Actions>
                        <Button onClick={() => setCancelTarget(null)}>{t('memberships.keepButton')}</Button>
                        <Button negative loading={cancelling} disabled={cancelling} onClick={handleConfirmCancel}>
                            {t('memberships.confirmButton')}
                        </Button>
                    </Modal.Actions>
                </Modal>
            )}

            {subscribeTarget && (
                <Modal open onClose={closeSubscribe} size="small">
                    <Modal.Header>{t('memberships.subscribeTitle', { name: subscribeTarget.name })}</Modal.Header>
                    <Modal.Content>
                        {preparingPayment && (
                            <div style={{ padding: '2rem', textAlign: 'center' }}>
                                <Loader active inline="centered" content={t('memberships.preparingPayment')} />
                            </div>
                        )}
                        {prepareError && (
                            <Message negative>
                                <Message.Header>{t('memberships.paymentError')}</Message.Header>
                                <p>{prepareError}</p>
                            </Message>
                        )}
                        {subscribed && (
                            <Message positive>
                                <Message.Header>{t('memberships.subscriptionActivated')}</Message.Header>
                                <p>{t('memberships.subscriptionSuccess')}</p>
                            </Message>
                        )}
                        {!preparingPayment && !prepareError && !subscribed && clientSecret && stripePromise && (
                            <Elements stripe={stripePromise} options={{ clientSecret }}>
                                <SubscribeForm
                                    membership={subscribeTarget}
                                    profile={profile}
                                    onSubscribed={async () => {
                                        await refreshCurrent();
                                        setSubscribed(true);
                                    }}
                                />
                            </Elements>
                        )}
                    </Modal.Content>
                    {!subscribed && (
                        <Modal.Actions>
                            <Button onClick={closeSubscribe}>{t('memberships.cancelButton')}</Button>
                        </Modal.Actions>
                    )}
                    {subscribed && (
                        <Modal.Actions>
                            <Button style={{ backgroundColor: 'var(--colour_menu_main)', color: '#fff' }} onClick={closeSubscribe}>
                                {t('memberships.doneButton')}
                            </Button>
                        </Modal.Actions>
                    )}
                </Modal>
            )}
        </Container>
    );
}

export default Memberships;
